import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Col, Container, Row } from 'react-bootstrap';
import { Link, useLocation } from 'wouter';
import { IconArrowSmall } from '@medifind/icons';
import { IconClose } from '@medifind/icons';
import { wpPostsByCategory } from '@medifind/interface';
import { FAQPageJsonLd } from '@medifind/seo';
import { Button } from '@medifind/shared-basic-components';
import { MFLink } from '@medifind/shared-basic-components';
import { isPrerender } from '@medifind/utils';
import { scrollToId } from '@medifind/utils';
import Down from '../assets/images/down.svg';
import styles from './Splash.module.scss';

export const Splash = ({ icon, action, title, subtitle, description, swapActionDescription }) => {
  const [location] = useLocation();
  const route = location;

  const titleColor = {
    '/symptom-checker': 'gradient-1',
    '/condition-search': 'gradient-2',
    '/find-a-doctor': 'gradient-3',
    '/second-opinion': 'gradient-4',
    '/latest-research': 'gradient-5',
    '/clinical-trials': 'gradient-6',
  }[route];

  return (
    <div className={styles['splash']}>
      <Row className={styles['splash-landing']}>
        <Col lg={{ span: 3, order: 1 }} sm={{ span: 12, order: 4 }} xs={{ span: 12, order: 4 }} className={styles['legend-section']}>
          <div className={styles['title']}>MediFind can help you find better care, faster.</div>
          <ul className={styles['list']}>
            <li>
              {route === '/symptom-checker' ? (
                <div className={styles['pin']}>
                  <PinBullet gradientNumber="1" />
                </div>
              ) : (
                <div className={classNames(styles['dot'], styles['gradient-1'])} />
              )}
              <Link to="/symptom-checker" className={classNames(styles['text'], route === '/symptom-checker' && styles['gradient-1'])}>
                Symptom Checker
              </Link>
            </li>
            <li>
              {route === '/condition-search' ? (
                <div className={styles['pin']}>
                  <PinBullet gradientNumber="2" />
                </div>
              ) : (
                <div className={classNames(styles['dot'], styles['gradient-2'])} />
              )}
              <Link to="/condition-search" className={classNames(styles['text'], route === '/condition-search' && styles['gradient-2'])}>
                Condition Search
              </Link>
            </li>
            <li>
              {route === '/find-a-doctor' ? (
                <div className={styles['pin']}>
                  <PinBullet gradientNumber="3" />
                </div>
              ) : (
                <div className={classNames(styles['dot'], styles['gradient-3'])} />
              )}
              <Link to="/find-a-doctor" className={classNames(styles['text'], route === '/find-a-doctor' && styles['gradient-3'])}>
                Find a Doctor
              </Link>
            </li>
            <li>
              {route === '/second-opinion' ? (
                <div className={styles['pin']}>
                  <PinBullet gradientNumber="4" />
                </div>
              ) : (
                <div className={classNames(styles['dot'], styles['gradient-4'])} />
              )}
              <Link to="/second-opinion" className={classNames(styles['text'], route === '/second-opinion' && styles['gradient-4'])}>
                Second Opinion
              </Link>
            </li>
            <li>
              {route === '/latest-research' ? (
                <div className={styles['pin']}>
                  <PinBullet gradientNumber="5" />
                </div>
              ) : (
                <div className={classNames(styles['dot'], styles['gradient-5'])} />
              )}
              <Link to="/latest-research" className={classNames(styles['text'], route === '/latest-research' && styles['gradient-5'])}>
                Latest Advances
              </Link>
            </li>
            <li>
              {route === '/clinical-trials' ? (
                <div className={styles['pin']}>
                  <PinBullet gradientNumber="6" />
                </div>
              ) : (
                <div className={classNames(styles['dot'], styles['gradient-6'])} />
              )}
              <Link to="/clinical-trials" className={classNames(styles['text'], route === '/clinical-trials' && styles['gradient-6'])}>
                Clinical Trials
              </Link>
            </li>
          </ul>
        </Col>
        <Col lg={{ span: 1, order: 2 }} sm={{ span: 12, order: 1 }} xs={{ span: 12, order: 1 }} className={styles['icon-section']}>
          {icon}
        </Col>
        <Col lg={{ order: 3 }} sm={{ span: 12, order: 2 }} xs={{ span: 12, order: 2 }} className={styles['title-section']}>
          <h1 className={classNames(styles['title'], styles[titleColor])}>{title}</h1>
          <div className={classNames({ [styles['subtitle-description-top']]: !!swapActionDescription })}>
            <div className={styles['subtitle']}>{subtitle}</div>
            <div className={styles['description']}>{description}</div>
          </div>
          {action}
          <div className={classNames(styles['subtitle-description-bottom'], { [styles['hide']]: !swapActionDescription })}>
            <div className={styles['subtitle']}>{subtitle}</div>
            <div className={styles['description']}>{description}</div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <button className={classNames(styles['learn-more'], styles['other'])} onClick={() => scrollToId('blurb')} type="button">
            {`Learn More\nAbout MediFind`}
            <img src={Down} alt="Learn More" />
          </button>
        </Col>
      </Row>
    </div>
  );
};

const PinBullet = ({ gradientNumber }) => (
  <svg width="11" height="13" viewBox="0 0 11 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="paint1_linear" x1="11" y1="0" x2="-1.82069" y2="10.8483" gradientUnits="userSpaceOnUse">
        <stop stopColor="#CD3758" />
        <stop offset="1" stopColor="#E5243E" />
      </linearGradient>
      <linearGradient id="paint2_linear" x1="11" y1="0" x2="-1.82069" y2="10.8483" gradientUnits="userSpaceOnUse">
        <stop stopColor="#b94b72" />
        <stop offset="1" stopColor="#CD3758" />
      </linearGradient>
      <linearGradient id="paint3_linear" x1="11" y1="0" x2="-1.82069" y2="10.8483" gradientUnits="userSpaceOnUse">
        <stop stopColor="#835692" />
        <stop offset="1" stopColor="#A54F7E" />
      </linearGradient>
      <linearGradient id="paint4_linear" x1="11" y1="0" x2="-1.82069" y2="10.8483" gradientUnits="userSpaceOnUse">
        <stop stopColor="#715a9c" />
        <stop offset="1" stopColor="#89558e" />
      </linearGradient>
      <linearGradient id="paint5_linear" x1="11" y1="0" x2="-1.82069" y2="10.8483" gradientUnits="userSpaceOnUse">
        <stop stopColor="#595faa" />
        <stop offset="1" stopColor="#715a9c" />
      </linearGradient>
      <linearGradient id="paint6_linear" x1="11" y1="0" x2="-1.82069" y2="10.8483" gradientUnits="userSpaceOnUse">
        <stop stopColor="#4062bb" />
        <stop offset="1" stopColor="#595faa" />
      </linearGradient>
    </defs>
    <path
      d="M9.39203 1.59575C7.27805 -0.537586 3.72476 -0.526238 1.61078 1.59575C-0.536928 3.76312 -0.536928 7.28085 1.61078 9.44823L4.87171 12.739C5.05162 12.9092 5.27651 13 5.50141 13C5.7263 13 5.96243 12.9092 6.1311 12.739L9.39203 9.44823C10.4265 8.39291 11 6.99716 11 5.52199C11 4.03546 10.4265 2.63972 9.39203 1.59575Z"
      fill={`url(#paint${gradientNumber}_linear)`}
    />
  </svg>
);

export const SplashFAQs = ({ blurbTitle, blurbBody, slug }) => {
  const [faq, setFAQ] = useState([]);

  const FAQ = ({ question, answer }) => {
    const [expanded, setExpanded] = useState(isPrerender);
    return (
      <Row className={styles['faq']} onClick={() => setExpanded(!expanded)}>
        <Col className={styles['faq-question']}>
          <h2>{question}</h2>
        </Col>
        <Col
          onClick={(e) => e.stopPropagation()}
          className={styles['faq-answer']}
          dangerouslySetInnerHTML={expanded ? { __html: answer } : { __html: null }}
        />
        <div className={classNames(styles['faq-button'], { [styles['expanded']]: expanded })}>
          <Button
            icon={<IconClose size="xlarge" strokeWidth="thin" />}
            focus="outline"
            buttonProps={{ 'aria-expanded': expanded }}
            screenReaderText="See More"
          />
        </div>
      </Row>
    );
  };
  useEffect(() => {
    wpPostsByCategory({
      category: slug,
      perPage: 100,
      page: 1,
      getContent: true,
    })
      .then((a) => {
        const faq = a.results.map((p) => ({
          question: p.title.rendered.trim(),
          answer: p.content.rendered.trim().replace(/>[\n\r]+</g, '><'),
        }));
        setFAQ(faq);
      })
      .catch(() => null);
    // }
  }, [slug]);

  return (
    <div className={styles['splash']}>
      {faq.length > 0 && (
        <FAQPageJsonLd
          mainEntity={faq.map((d) => ({
            questionName: d.question,
            acceptedAnswerText: d.answer,
          }))}
        />
      )}
      <Container>
        <Row id="blurb" className={styles['blurb']}>
          <Col>
            <h2 className={styles['blurb-title']}>{blurbTitle}</h2>
            <div className={styles['blurb-body']}>{blurbBody}</div>
          </Col>
        </Row>
        {faq.map((faq, i) => (
          <FAQ key={i} {...faq} />
        ))}
        <Row className={styles['contact-us']}>
          <Col>
            <div className={styles['contact-us-title']}>Still have questions?</div>
            <MFLink to="/contact" noTabIndex>
              <Button color="blue" label="Contact Us" icon={<IconArrowSmall color="white" />} />
            </MFLink>
          </Col>
        </Row>
      </Container>
    </div>
  );
};
